import React from 'react';
import { globals, productLinkBuilder } from 'common-ui';
import { typeaheadCheck } from '../utils';
import { ProductItem } from '../productCard/productItem';
import type { BaseItem } from './types';
import type { GetSourcesParams, OnSelectParams } from '@algolia/autocomplete-core';
import type { RecommendClient } from '@algolia/recommend';
import { storeQueryIDForProduct } from '../../../utils';
import { getRecommendClient } from '../../../utils/recommendClient';

const TrendingPluginHeader = (sourceQuery: string, hasTrendingItems: boolean) => (
  <div>
    <p className="no-results-message">
      <strong>Your search “{sourceQuery}” did not match any Products</strong>
    </p>
    {hasTrendingItems && <span className="aa-SourceHeaderTitle">Trending</span>}
  </div>
);

const TrendingPluginItem = ({ item, components }: any) => {
  if (item.__isEmpty) {
    return <div className="no-results-message" />;
  }
  return <ProductItem hit={item} components={components} />;
};

const fetchTrendingItems = async (client: RecommendClient) => {
  try {
    const { results } = await client.getRecommendations({
      requests: [
        {
          indexName: globals.algoliaIndexName as string,
          model: 'trending-items',
          maxRecommendations: 5,
          threshold: 0,
        },
      ],
    });
    return results[0]?.hits ?? [];
  } catch {
    return [];
  }
};

const buildTrendingSource = (sourceQuery: string, client: RecommendClient) => ({
  sourceId: 'trendingPlugin',
  async getItems() {
    const trendingHits = await fetchTrendingItems(client);
    return trendingHits.length > 0 ? trendingHits : [{ __isEmpty: true }];
  },
  onSelect({ item, state }: OnSelectParams<BaseItem>) {
    if (item?.productSlug && item?.itemCode) {
      storeQueryIDForProduct(
        item?.itemCode as string,
        state?.context?.queryID as string,
        0,
        item.__autocomplete_indexName as string,
      );

      window.location.href = productLinkBuilder(item.productSlug as string, item.itemCode as string);
    }
  },
  templates: {
    header({ items }: { items: BaseItem[] }) {
      const hasTrendingItems = items.length > 0 && !items[0].__isEmpty;
      return TrendingPluginHeader(sourceQuery, hasTrendingItems);
    },
    item: TrendingPluginItem,
  },
});

export function createTrendingPlugin() {
  const recommendClient = getRecommendClient();
  return {
    getSources({ query: sourceQuery, state }: GetSourcesParams<BaseItem>) {
      if (!sourceQuery) {
        return [];
      }

      if (!typeaheadCheck(sourceQuery)) {
        return [];
      }

      if (state.context?.noResultsFound) {
        return [buildTrendingSource(sourceQuery, recommendClient)];
      }

      return []; // Also return empty if not noResultsFound
    },
  };
}
